import React from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Chip, DsButton } from "../../ds";

export const SOCIAL_STATUSES = [
  "waiting_for_video", "production_approved", "producing", "production_failed",
  "waiting_visual_approval", "visual_rejected", "approved_waiting_n8n", "published", "failed",
  "blocked_needs_ceo_fact", "needs_asset", "archived",
];

export const STATUS_COLOR = {
  waiting_visual_approval: "var(--violet)",
  visual_rejected: "var(--bad)",
  approved_waiting_n8n: "#2EB8EF",
  published: "var(--ok)",
  failed: "var(--bad)",
  blocked_needs_ceo_fact: "var(--warn)",
  needs_asset: "var(--warn)",
  waiting_for_video: "var(--warn)",
  production_approved: "#2EB8EF",
  producing: "var(--violet)",
  production_failed: "var(--bad)",
  archived: "var(--dim)",
};

export function dubaiTime(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(iso));
}

const ResultCell = ({ item, onChanged }) => {
  const r = item.result;
  if (item.status === "published" && r?.platform_url) {
    return <a href={r.platform_url} target="_blank" rel="noreferrer" className="text-[color:var(--cobalt)] underline" data-testid={`queue-link-${item.id}`}>View post</a>;
  }
  if (item.status === "archived") {
    const restore = async () => {
      try { await api.post(`/social/items/${encodeURIComponent(item.id)}/restore`); toast.success("Restored."); onChanged(); }
      catch (e) { toast.error(formatApiError(e)); }
    };
    return (
      <div className="flex flex-col gap-1">
        {item.archive_reason && <span className="text-xs text-[color:var(--muted)]" title={item.archive_reason} data-testid={`queue-archive-reason-${item.id}`}>Reason: {item.archive_reason}</span>}
        <DsButton className="h-7 w-fit" onClick={restore} data-testid={`queue-restore-${item.id}`}>Restore</DsButton>
      </div>
    );
  }
  if (item.status !== "failed") return <span className="text-[color:var(--muted)]">—</span>;
  const call = async (path, msg) => {
    try { await api.post(`/social/items/${encodeURIComponent(item.id)}/${path}`); toast.success(msg); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[color:var(--bad)] text-xs" data-testid={`queue-error-${item.id}`}>{r?.error || "Publishing failed."}</span>
      <div className="flex gap-2">
        {!item.failure_ack_at && (
          <DsButton className="h-7 w-fit" onClick={() => call("ack", "Dismissed.")} data-testid={`queue-dismiss-${item.id}`}>Dismiss</DsButton>
        )}
        <DsButton variant="go" className="h-7 w-fit" onClick={() => call("retry", "Queued for n8n again.")} data-testid={`queue-retry-${item.id}`}>Retry</DsButton>
      </div>
    </div>
  );
};

export const QueueTable = ({ items, brandNames, onChanged }) => (
  <div className="overflow-auto max-h-[480px] rounded-[6px]" data-testid="social-queue-table">
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-10 bg-[color:var(--panel)]">
        <tr className="text-left font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] border-b border-[color:var(--line)]">
          {["When (Dubai)", "Brand", "Channel", "Caption", "Media", "Status", "Result"].map((h) => (
            <th key={h} className="px-3 py-2">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr><td colSpan={7} className="px-3 py-10 text-center text-[color:var(--muted)]" data-testid="social-queue-empty">No queue items for these filters.</td></tr>
        )}
        {items.map((it) => (
          <tr
            key={it.id}
            className="border-b border-[color:var(--line)] align-top"
            style={it.status === "failed" && !it.failure_ack_at ? { background: "rgba(255,107,107,0.08)", boxShadow: "inset 3px 0 0 var(--bad)" } : undefined}
            data-testid={`queue-row-${it.id}`}
          >
            <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{dubaiTime(it.scheduled_at)}</td>
            <td className="px-3 py-3">{brandNames[it.brand_key] || it.brand || it.project_slug}</td>
            <td className="px-3 py-3 font-mono text-xs">{it.channel}</td>
            <td className="px-3 py-3 max-w-[340px]"><p className="line-clamp-2">{it.caption || "—"}</p></td>
            <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{(it.media_urls || []).length} {it.media_type || ""}</td>
            <td className="px-3 py-3"><Chip value={it.status === "production_approved" ? "Approved for production" : it.status} color={STATUS_COLOR[it.status]} testId={`queue-status-${it.id}`} /></td>
            <td className="px-3 py-3"><ResultCell item={it} onChanged={onChanged} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
